import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, AlertTriangle, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const PlaneGame = ({ id = "game" }) => {
  const canvasRef = React.useRef(null);
  const gameContainerRef = React.useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = React.useRef();

  // Game Constants
  const GRAVITY = 0.25;
  const JUMP = -4.5;
  const OBSTACLE_SPEED = 3.5;
  const OBSTACLE_SPAWN_RATE = 100; // frames
  const PLANE_SIZE = 40;

  // Game State Refs (to avoid re-renders in loop)
  const planeRef = React.useRef({ y: 200, velocity: 0, rotation: 0 });
  const obstaclesRef = React.useRef([]);
  const frameCountRef = React.useRef(0);
  const particlesRef = React.useRef([]);

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Refs for dynamic dimensions
  const canvasWidth = React.useRef(800);
  const canvasHeight = React.useRef(500);

  useEffect(() => {
    const saved = localStorage.getItem("meraki_plane_high_score");
    if (saved) setHighScore(parseInt(saved));

    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (gameContainerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = gameContainerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        canvasRef.current.width = canvasWidth.current;
        canvasRef.current.height = canvasHeight.current;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = gameContainerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const initGame = useCallback(() => {
    planeRef.current = { y: 200, velocity: 0, rotation: 0 };
    obstaclesRef.current = [];
    frameCountRef.current = 0;
    particlesRef.current = [];
    setScore(0);
    setGameState("playing");
  }, []);

  const createParticles = (x, y) => {
    for (let i = 0; i < 30; i++) {
      particlesRef.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 10,
        vy: (Math.random() - 0.5) * 10,
        size: Math.random() * 5 + 2,
        life: 1.0,
        color: Math.random() > 0.5 ? "#c9a961" : "#ff4d00"
      });
    }
  };

  const handleJump = useCallback(() => {
    if (gameState === "playing") {
      planeRef.current.velocity = JUMP;
    } else if (gameState === "start" || gameState === "gameOver") {
      initGame();
    }
  }, [gameState, JUMP, initGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const update = () => {
      if (gameState !== "playing") return;

      const p = planeRef.current;
      p.velocity += GRAVITY;
      p.y += p.velocity;
      p.rotation = Math.min(Math.PI / 4, Math.max(-Math.PI / 4, p.velocity * 0.1));

      // Spawn Obstacles
      frameCountRef.current++;
      if (frameCountRef.current % OBSTACLE_SPAWN_RATE === 0) {
        const gap = 160;
        const minHeight = 50;
        const h = minHeight + Math.random() * (canvasHeight.current - gap - minHeight * 2);
        obstaclesRef.current.push({
          x: canvasWidth.current,
          top: h,
          bottom: canvasHeight.current - h - gap,
          passed: false
        });
      }

      // Update Obstacles
      obstaclesRef.current.forEach(obs => {
        obs.x -= OBSTACLE_SPEED;

        // Score
        if (!obs.passed && obs.x < 50) {
          obs.passed = true;
          setScore(s => s + 1);
        }

        // Collision
        const planeBox = { x: 50, y: p.y, w: PLANE_SIZE, h: PLANE_SIZE - 10 };
        const topBox = { x: obs.x, y: 0, w: 60, h: obs.top };
        const botBox = { x: obs.x, y: canvasHeight.current - obs.bottom, w: 60, h: obs.bottom };

        if (
          (planeBox.x < topBox.x + topBox.w &&
            planeBox.x + planeBox.w > topBox.x &&
            planeBox.y < topBox.y + topBox.h) ||
          (planeBox.x < botBox.x + botBox.w &&
            planeBox.x + planeBox.w > botBox.x &&
            planeBox.y + planeBox.h > botBox.y)
        ) {
          setGameState("gameOver");
          createParticles(planeBox.x + 20, planeBox.y + 15);
        }
      });

      // Boundaries
      if (p.y < 0 || p.y + PLANE_SIZE > canvasHeight.current) {
        setGameState("gameOver");
        createParticles(50, p.y < 0 ? 0 : canvasHeight.current);
      }

      // Remove off-screen obstacles
      obstaclesRef.current = obstaclesRef.current.filter(obs => obs.x > -100);
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);

      // 1. Background Architectural Grid
      ctx.strokeStyle = "rgba(201,169,97,0.05)";
      ctx.lineWidth = 0.5;
      const gridSize = 40;
      for (let x = 0; x < canvasWidth.current; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvasWidth.current); ctx.stroke();
      }
      for (let y = 0; y < canvasHeight.current; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvasWidth.current, y); ctx.stroke();
      }

      // 2. Draw Obstacles (Monolithic Data Towers)
      obstaclesRef.current.forEach(obs => {
        const obsGrad = ctx.createLinearGradient(obs.x, 0, obs.x + 60, 0);
        obsGrad.addColorStop(0, "rgba(201,169,97,0.02)");
        obsGrad.addColorStop(0.5, "rgba(201,169,97,0.12)");
        obsGrad.addColorStop(1, "rgba(201,169,97,0.02)");

        ctx.fillStyle = obsGrad;
        ctx.strokeStyle = "rgba(201,169,97,0.25)";
        ctx.lineWidth = 1.5;

        // Top monolith
        ctx.fillRect(obs.x, 0, 60, obs.top);
        ctx.strokeRect(obs.x, 0, 60, obs.top);
        
        // Add tech-lines to monolith
        ctx.strokeStyle = "rgba(201,169,97,0.08)";
        for(let j = 40; j < obs.top; j += 40) {
          ctx.beginPath();
          ctx.moveTo(obs.x, j);
          ctx.lineTo(obs.x + 60, j);
          ctx.stroke();
        }

        // Bottom monolith
        ctx.fillRect(obs.x, canvasHeight.current - obs.bottom, 60, obs.bottom);
        ctx.strokeRect(obs.x, canvasHeight.current - obs.bottom, 60, obs.bottom);

        for(let j = canvasHeight.current - obs.bottom + 40; j < canvasHeight.current; j += 40) {
          ctx.beginPath();
          ctx.moveTo(obs.x, j);
          ctx.lineTo(obs.x + 60, j);
          ctx.stroke();
        }
      });

      // Draw Plane (Meraki Cinematic Craft)
      const p = planeRef.current;
      ctx.save();
      ctx.translate(50 + PLANE_SIZE / 2, p.y + PLANE_SIZE / 2);
      ctx.rotate(p.rotation);

      // 1. Glow / Bloom Effect
      ctx.shadowBlur = 25;
      ctx.shadowColor = "#e4c77a";

      // 2. Fuselage (Sleek aerodynamic shape)
      const fuselageGrad = ctx.createLinearGradient(-15, 0, 20, 0);
      fuselageGrad.addColorStop(0, "#b87333"); // Copper
      fuselageGrad.addColorStop(0.5, "#e4c77a"); // Gold
      fuselageGrad.addColorStop(1, "#f2d99c"); // Highlight

      ctx.fillStyle = fuselageGrad;
      ctx.beginPath();
      ctx.moveTo(-18, -4);
      ctx.lineTo(22, 0);
      ctx.lineTo(-18, 4);
      ctx.lineTo(-12, 0);
      ctx.closePath();
      ctx.fill();

      // 3. Dual-Tier Wings (Geometric & Sharp)
      ctx.fillStyle = "#e4c77a";
      ctx.shadowBlur = 10;
      
      // Top Wing
      ctx.beginPath();
      ctx.moveTo(-8, -2);
      ctx.lineTo(-4, -18);
      ctx.lineTo(8, -18);
      ctx.lineTo(6, -2);
      ctx.closePath();
      ctx.fill();
      
      // Bottom Wing
      ctx.beginPath();
      ctx.moveTo(-8, 2);
      ctx.lineTo(-4, 18);
      ctx.lineTo(8, 18);
      ctx.lineTo(6, 2);
      ctx.closePath();
      ctx.fill();

      // 4. Cockpit / Core (High Contrast)
      ctx.fillStyle = "#fff";
      ctx.shadowBlur = 15;
      ctx.shadowColor = "#fff";
      ctx.beginPath();
      ctx.ellipse(2, 0, 7, 2, 0, 0, Math.PI * 2);
      ctx.fill();

      // 5. Engine Pulse (Animated Thruster)
      if (gameState === "playing") {
        ctx.shadowBlur = 30;
        ctx.shadowColor = "#e4c77a";
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(-14, 0, 4 + Math.sin(Date.now() * 0.02) * 2, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      // Draw Particles
      particlesRef.current.forEach((part, i) => {
        ctx.globalAlpha = part.life;
        ctx.fillStyle = part.color;
        ctx.beginPath();
        ctx.arc(part.x, part.y, part.size, 0, Math.PI * 2);
        ctx.fill();
        
        part.x += part.vx;
        part.y += part.vy;
        part.life -= 0.02;
        part.size *= 0.98;
      });
      ctx.globalAlpha = 1.0;
      particlesRef.current = particlesRef.current.filter(p => p.life > 0);

      if (gameState === "playing") {
        requestRef.current = requestAnimationFrame(() => {
          update();
          draw();
        });
      } else if (gameState === "gameOver" && particlesRef.current.length > 0) {
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    if (gameState === "playing") {
      requestRef.current = requestAnimationFrame(draw);
    } else if (gameState === "gameOver") {
      requestRef.current = requestAnimationFrame(draw);
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem("meraki_plane_high_score", score.toString());
      }
    }

    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, score, highScore]);

  // Handle Controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        handleJump();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState, handleJump]);

  return (
    <section id={id} className="py-24 relative overflow-hidden" style={{ background: "#0a0908" }}>
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="10" label="Interactive" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Meraki <span className="italic gold-text">Sky Drift</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Avoid obstacles. reach the peak.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={gameContainerRef}
          className="relative w-full aspect-[3/4] md:aspect-video rounded-3xl overflow-hidden bg-black/40 border border-white/10 group"
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          onClick={handleJump}
        >
          
          <canvas 
            ref={canvasRef} 
            width={1200} 
            height={675}
            className="w-full h-full"
          />

          {/* Overlays */}
          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                {/* Thumbnail Background */}
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/images/sky_drift_thumb.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/70 backdrop-blur-[2px]" />

                <div className="relative z-20 flex flex-col items-center">
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    className="p-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/20 mb-8 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); initGame(); }}
                  >
                    <Play className="text-[var(--gold)] w-12 h-12 fill-[var(--gold)]" />
                  </motion.div>
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-4 italic text-center leading-tight">Ready for Takeoff?</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-widest uppercase mb-10 text-center leading-relaxed">
                    {isTouchDevice ? "Tap to fly" : "Click or Space to fly"} — Avoid the obstacles
                  </p>
                  <button 
                    className="btn-primary scale-110" 
                    onClick={(e) => { e.stopPropagation(); initGame(); }}
                  >
                    Start Mission
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-md z-30"
              >
                <AlertTriangle className="text-red-500 w-16 h-16 mb-6" />
                <h3 className="font-display text-5xl text-white mb-2">MISSION FAILED</h3>
                <p className="text-red-400 font-impact text-lg tracking-widest uppercase mb-8">Structural Collapse Detected</p>
                
                <div className="flex gap-12 mb-10">
                  <div className="text-center">
                    <div className="text-[10px] text-[var(--muted)] uppercase tracking-tighter mb-1">SCORE</div>
                    <div className="text-4xl font-display text-white">{score}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-[var(--muted)] uppercase tracking-tighter mb-1">BEST</div>
                    <div className="text-4xl font-display text-[var(--gold)]">{highScore}</div>
                  </div>
                </div>

                <button className="btn-primary" onClick={(e) => { e.stopPropagation(); initGame(); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Try Again
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* In-game HUD */}
          {gameState === "playing" && (
            <div className="absolute top-8 left-8 flex gap-8 z-20 pointer-events-none">
              <div>
                <div className="text-[10px] text-[var(--gold)] uppercase tracking-widest mb-1">ALTITUDE</div>
                <div className="text-2xl font-impact text-white">{score}m</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Meraki Sky Drift"
        instructions={[
          { title: "Control the Craft", desc: "Your Meraki Sky Craft is in constant descent. Click or tap anywhere to trigger the thrusters and gain altitude." },
          { title: "Navigate Monoliths", desc: "Fly between the gaps of the towering data monoliths. Touching any part of a monolith will cause a structural collapse." },
          { title: "Maintain Altitude", desc: "Don't fly too high or hit the ground. Stay within the operational grid to continue the mission." },
          { title: "Score Distance", desc: "Each set of monoliths passed successfully adds to your flight distance. Aim for the peak score!" }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default PlaneGame;
